import Feature from 'ol/Feature';
import GeoJSON from 'ol/format/GeoJSON';
import { Fill, Stroke, Style } from 'ol/style';

import { resolveObjects } from './collectionMapStyle';
import { FALLBACK_OBJECT_COLOR } from '../constants';

/**
 * Continuous things — a road's centerlines and its surface, a river's area — saved by a Find
 * segment search. They are not detections with a box, a centre and an outline: a centerline is a
 * LineString the three shape layers cannot draw (it would fall back to a dot at its centroid), and
 * a road surface is one large polygon with holes that WebGL's triangulation breaks into shards.
 * So they get their own canvas layer: centerlines as lines, surfaces as a light fill.
 */

const LINE_TYPES = new Set(['LineString', 'MultiLineString']);
const SEGMENT_KINDS = new Set(['area', 'centerline', 'link']);

const LINE_WIDTH = 3;
const HALO = 'rgba(0, 0, 0, 0.45)';
const AREA_FILL_OPACITY = 0.22;

const format = new GeoJSON();

/** Whether a GeoJSON feature is part of a continuous thing rather than a detection. */
export const isSegmentFeature = feature =>
	LINE_TYPES.has(feature?.geometry?.type) || SEGMENT_KINDS.has(feature?.properties?.kind);

/**
 * Splits features into detections (for the shape layers) and segment parts.
 * @param {Array} features
 * @returns {{ detections: Array, segments: Array }}
 */
export const splitSegments = features => {
	const detections = [];
	const segments = [];
	for (const feature of features ?? [])
		(isSegmentFeature(feature) ? segments : detections).push(feature);
	return { detections, segments };
};

/** Builds the OL feature for one segment part, its own geometry as it is; null when unusable. */
export const buildSegmentFeature = feature => {
	if (!feature?.geometry) return null;
	try {
		const geometry = format.readGeometry(feature.geometry);
		return new Feature({ geometry, ...(feature.properties ?? {}) });
	} catch {
		return null;
	}
};

const rgba = (hex, alpha) => {
	const full = /^#[0-9a-f]{6}$/i.test(hex ?? '') ? hex : FALLBACK_OBJECT_COLOR;
	const [r, g, b] = [1, 3, 5].map(i => parseInt(full.slice(i, i + 2), 16));
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Style function for the segment layer: each object in its own colour, lines over a dark halo
 * (dashed where bridged under canopy), surfaces filled lightly with no outline.
 *
 * @param {Array<{key, color?, opacity?}>} objects
 * @param {{ objectKey, color?, opacity? }|null} liveStyling
 * @returns {(feature: Feature) => Style[]}
 */
export const buildSegmentStyle = (objects, liveStyling = null) => {
	const byKey = new Map(resolveObjects(objects, liveStyling).map(o => [o.key, o]));
	const cache = new Map();
	return feature => {
		const key = feature.get('node_uuid');
		const line = LINE_TYPES.has(feature.getGeometry()?.getType());
		const dashed = Boolean(feature.get('inferred'));
		const id = `${key}|${line}|${dashed}`;
		if (!cache.has(id)) {
			const { color, alpha = 1 } = byKey.get(key) ?? {};
			cache.set(
				id,
				line
					? [
							new Style({
								stroke: new Stroke({ color: HALO, width: LINE_WIDTH + 3 }),
								zIndex: 0,
							}),
							new Style({
								stroke: new Stroke({
									color: rgba(color, alpha),
									width: LINE_WIDTH,
									lineDash: dashed ? [8, 6] : undefined,
								}),
								zIndex: 1,
							}),
						]
					: [
							new Style({
								fill: new Fill({ color: rgba(color, alpha * AREA_FILL_OPACITY) }),
							}),
						]
			);
		}
		return cache.get(id);
	};
};
